import React from 'react';
import clsx from 'clsx';
import { ArrowTurnDownRightIcon, MagnifyingGlassIcon } from '@heroicons/react/16/solid';

import {
  bundleLabel,
  entityLabel,
  fieldLabel,
  liveSource,
  opLabel,
  resultDetail,
  resultLabel,
  searchOutcome,
  searchView,
  valueLabel,
  type FilterView,
  type SearchIntegrationTool
} from '@oysterjs/ais/components/workspaces/lib/search-integration';

type Leaf = Extract<FilterView, { kind: 'leaf' }>;
type Group = Extract<FilterView, { kind: 'group' }>;

/** How many found records the step names before counting the rest. */
const NAMED_RESULTS = 3;

/**
 * Conditions, and then the names of what came back, rise in one after another,
 * so the query reads as it is composed. The delay stops growing after a dozen
 * so a long filter does not drag.
 */
const rise = (order: number): React.CSSProperties => ({
  animationDelay: `${Math.min(order, 12) * 30}ms`
});

/**
 * One line of the step. Every line shares a glyph column, and the glyph sits
 * in a box one line tall, so it centres on the first line when the content
 * wraps and every line's content starts at the same x.
 */
const Line = (props: { glyph?: React.ReactNode; wrap?: boolean; children: React.ReactNode }) => (
  <>
    <span aria-hidden className="flex h-5 items-center justify-center">
      {props.glyph}
    </span>
    <div
      className={clsx(
        'flex min-w-0 items-center gap-x-1 gap-y-0.5',
        props.wrap === false ? 'flex-nowrap' : 'flex-wrap'
      )}
    >
      {props.children}
    </div>
  </>
);

const Glyph = ({ icon: Icon }: { icon: React.ComponentType<{ className?: string }> }) => (
  <Icon className="size-3.5 text-neutral-400 dark:text-neutral-500" />
);

/** The outcome's glyph: its colour says where the search stands. */
const Dot = ({ tone }: { tone: 'pending' | 'live' | 'synced' | 'error' }) => (
  <span className="relative flex size-1.5">
    {tone === 'live' && (
      <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-60 motion-safe:animate-ping motion-safe:[animation-iteration-count:1]" />
    )}
    <span
      className={clsx(
        'relative size-1.5 rounded-full',
        tone === 'pending' && 'bg-blue-500',
        tone === 'live' && 'bg-emerald-500',
        tone === 'synced' && 'bg-neutral-300 dark:bg-neutral-600',
        tone === 'error' && 'bg-red-500'
      )}
    />
  </span>
);

/*
 * The step's text follows a markdown row body: one body colour, with weight
 * rather than colour for emphasis, and the timeline's meta colour for the
 * words that join a sentence. Values are chips like the app's others, and red
 * is left to the error dot.
 */
const Muted = ({ children }: { children: React.ReactNode }) => (
  <span className="text-neutral-400 dark:text-neutral-500">{children}</span>
);

const Strong = ({ className, children }: { className?: string; children: React.ReactNode }) => (
  <span className={clsx('font-medium', className)}>{children}</span>
);

const Value = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex h-[18px] items-center rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 font-medium text-neutral-700 dark:text-neutral-300">
    {children}
  </span>
);

const Condition = ({ leaf }: { leaf: Leaf }) => (
  <span
    className="inline-flex flex-wrap items-center gap-x-1 gap-y-0.5 motion-safe:animate-rise-in"
    style={rise(leaf.order)}
  >
    <span>{fieldLabel(leaf.field)}</span>
    <Muted>{opLabel(leaf.op, leaf.negate)}</Muted>
    {leaf.values.map((value, i) => (
      <Value key={i}>{valueLabel(value)}</Value>
    ))}
  </span>
);

/**
 * A group's children joined by its conjunction. Only a nested or negated group
 * is framed: the root's conjunction already reads as prose, and a frame round
 * the whole filter would say nothing.
 */
const ConditionGroup = (props: { group: Group; framed: boolean }) => {
  const { group } = props;
  const items = group.children.map((child, i) => (
    <React.Fragment key={i}>
      {i > 0 && <Muted>{group.match === 'all' ? 'and' : 'or'}</Muted>}
      <FilterNode view={child} framed />
    </React.Fragment>
  ));
  if (!props.framed && !group.negate) return <>{items}</>;
  return (
    <>
      {group.negate && <Muted>not</Muted>}
      <span
        data-testid="filter-group"
        className="-my-px inline-flex flex-wrap items-center gap-x-1 gap-y-0.5 rounded-md px-1 py-px ring-1 ring-inset ring-neutral-200 dark:ring-neutral-700"
      >
        {items}
      </span>
    </>
  );
};

const FilterNode = (props: { view: Leaf | Group; framed: boolean }) =>
  props.view.kind === 'leaf' ? (
    <Condition leaf={props.view} />
  ) : (
    <ConditionGroup group={props.view} framed={props.framed} />
  );

const Filter = (props: { view: FilterView; records: string }) =>
  props.view.kind === 'all' ? (
    <span>all {props.records}</span>
  ) : (
    <FilterNode view={props.view} framed={false} />
  );

const BundleList = ({ bundles }: { bundles: readonly string[] }) => (
  <>
    <Muted>with</Muted>
    <span>{bundles.map(bundleLabel).join(', ')}</span>
  </>
);

const Failure = ({ children }: { children: React.ReactNode }) => (
  <Line glyph={<Dot tone="error" />}>
    <Strong>{children}</Strong>
  </Line>
);

const Outcome = (props: { tool: SearchIntegrationTool; failed: boolean }) => {
  const outcome = searchOutcome(props.tool, props.failed);
  if (outcome.kind === 'failed') return <Failure>Search failed</Failure>;
  if (outcome.kind === 'pending') {
    return (
      <Line glyph={<Dot tone="pending" />}>
        <span className="bg-gradient-to-r from-neutral-500 via-neutral-700 to-neutral-500 dark:from-neutral-400 dark:via-neutral-300 dark:to-neutral-400 bg-[length:200%_100%] bg-clip-text text-transparent motion-safe:animate-shimmer">
          Searching…
        </span>
      </Line>
    );
  }

  if (outcome.kind === 'rejected') return <Failure>Search rejected</Failure>;

  const find = props.tool.input.find;
  const n = outcome.results.length;
  const named = outcome.results.slice(0, NAMED_RESULTS);
  const unnamed = n - named.length;

  return (
    <>
      <Line glyph={<Dot tone={outcome.source === 'LIVE' ? 'live' : 'synced'} />}>
        <Strong className="motion-safe:animate-rise-in">
          {n
            ? `${n}${outcome.hasMore ? '+' : ''} ${entityLabel(find, outcome.hasMore ? n + 1 : n)}`
            : 'No matches'}
        </Strong>
        {outcome.source === 'LIVE' && <Muted>{liveSource(outcome.results)}</Muted>}
        {outcome.source === 'SQL' && <Muted>from synced data</Muted>}
      </Line>
      {n > 0 && (
        <Line wrap={false}>
          <span className="min-w-0 truncate">
            {named.map((result, i) => (
              <React.Fragment key={i}>
                {i > 0 && ', '}
                <span
                  title={resultDetail(result) || undefined}
                  className="motion-safe:animate-rise-in"
                  style={rise(i + 1)}
                >
                  {resultLabel(result)}
                </span>
              </React.Fragment>
            ))}
          </span>
          {unnamed > 0 && (
            <span className="shrink-0">
              <Muted>+{unnamed} more</Muted>
            </span>
          )}
        </Line>
      )}
    </>
  );
};

/**
 * The body of a SEARCH_INTEGRATION step. The row's title already names the
 * entity, so the body opens with the filter and the bundles fetched with each
 * match, then each include, then what came back.
 *
 * Every line is 20px with 2px between lines, so a plain search (filter,
 * outcome, the named matches) is exactly ROW_COLLAPSED_HEIGHT_PX tall and
 * shows whole without expanding.
 */
export const SearchIntegrationStep = (props: { tool: SearchIntegrationTool; failed: boolean }) => {
  const { input } = props.tool;
  const { where, includes } = searchView(input);
  return (
    <div className="grid grid-cols-[14px_minmax(0,1fr)] gap-x-2 gap-y-0.5 pr-4 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
      <Line glyph={<Glyph icon={MagnifyingGlassIcon} />}>
        <Filter view={where} records={entityLabel(input.find)} />
        {input.bundles?.length ? <BundleList bundles={input.bundles} /> : null}
      </Line>
      {includes.map((include, i) => (
        <Line key={i} glyph={<Glyph icon={ArrowTurnDownRightIcon} />}>
          <Strong>{include.relation}</Strong>
          {include.where && (
            <>
              <Muted>where</Muted>
              <Filter view={include.where} records={include.relation} />
            </>
          )}
          {include.bundles?.length ? <BundleList bundles={include.bundles} /> : null}
        </Line>
      ))}
      <Outcome tool={props.tool} failed={props.failed} />
    </div>
  );
};
